/**
 * ComplianceReviewsQueue — Risk panel > Reviews > Compliance reviews (Bill 2026-11-04).
 *
 * The Innovation Hub change tracker's items for the Retail Operations Platform: changes that may affect compliance,
 * waiting for a compliance reviewer to confirm the impact or clear them. Tabs: Needs your review (flagged, not yet
 * reviewed), All open (still in progress), Reviewed, Everything. Each row says what the change is, why it was flagged
 * and where it stands; it opens the item's briefing (ComplianceReviewDetail).
 *
 * Server: /v3/risk/compliance-reviews/* (pcb-retail-api, on behalf of the signed-in person, to the Hub's app-review
 * routes). Who may review is the Hub's list (hub.compliance.reviewers); everyone else with the Risk panel sees the
 * items read-only.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { EmptyState, FilterChips, PageHeader, StatusChip } from '@retail-ops/panel-shell';
import { loadReviewItems, type ReviewRow, type ReviewView } from '@/services/complianceReviewsApi';
import { categoryWords, dayText, flagBadge, namesSentence, statusWords, waitedText } from './complianceModel';
import { useReviewSummary } from './useReviewSummary';
import './complianceReviews.css';

const VIEWS: ReadonlyArray<{ value: ReviewView; label: string; empty: string }> = [
  { value: 'needs-review', label: 'Needs your review', empty: 'Nothing is waiting for a compliance review.' },
  { value: 'open', label: 'All open', empty: 'No changes are in progress.' },
  { value: 'reviewed', label: 'Reviewed', empty: 'No change has been reviewed yet.' },
  { value: 'all', label: 'Everything', empty: 'The change tracker has no items for this platform yet.' },
];

const isView = (v: string | null): v is ReviewView => VIEWS.some((x) => x.value === v);

export function ReviewerNotice(): JSX.Element | null {
  const { summary } = useReviewSummary();
  if (!summary || summary.viewer.canReview) return null;
  return (
    <div className="banner banner--info cr-notice" role="note">
      <strong>You can read these items but not review them.</strong> Reviews are recorded only by the bank’s compliance
      reviewers: {namesSentence(summary.reviewers)}. A developer cannot review their own change.
      {summary.viewer.isDeveloper && ' You are listed as one of the developers.'}
    </div>
  );
}

function Row({ r, view }: { r: ReviewRow; view: ReviewView }): JSX.Element {
  const flag = flagBadge(r.complianceFlag);
  const since = r.filedAt ?? r.requestedAt;
  return (
    <li className="cr-row">
      <Link to={`/compliance-reviews/${encodeURIComponent(r.key)}`} className="cr-row__link">
        <div className="cr-row__top">
          <span className="cr-row__key">{r.key}</span>
          <StatusChip tone={flag.tone}>{flag.label}</StatusChip>
        </div>
        <p className="cr-row__title">{r.oneLiner?.trim() || 'No one-line summary yet'}</p>
        {r.complianceRationale && (
          <p className="cr-row__why">
            <span className="cr-label">Why it was flagged:</span> {r.complianceRationale}
          </p>
        )}
        {view === 'reviewed' && r.complianceReviewedBy && (
          <p className="cr-row__why">
            <span className="cr-label">Reviewed:</span> {flag.label} on {dayText(r.complianceReviewedAt)}
            {r.complianceNote ? ` — ${r.complianceNote}` : ''}
          </p>
        )}
        <p className="cr-row__meta">
          {statusWords(r.status)} · {categoryWords(r.category)} · reported {dayText(since)}
          {r.complianceFlag === 'agent-flagged' && ` · waiting ${waitedText(since)}`}
        </p>
      </Link>
    </li>
  );
}

export function ComplianceReviewsQueue(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const view: ReviewView = isView(params.get('view')) ? (params.get('view') as ReviewView) : 'needs-review';
  const { summary } = useReviewSummary();
  const [items, setItems] = useState<ReadonlyArray<ReviewRow> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    setItems(null);
    setError(null);
    try {
      setItems((await loadReviewItems(view)).items);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the change tracker.');
      setItems([]);
    }
  }, [view]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const counts = summary?.counts;
  const countFor = (v: ReviewView): number | undefined =>
    !counts ? undefined : v === 'needs-review' ? counts.needsReview : v === 'open' ? counts.open : v === 'reviewed' ? counts.reviewed : counts.all;
  const viewDef = VIEWS.find((v) => v.value === view)!;

  return (
    <div className="cr-page">
      <PageHeader
        title="Compliance reviews"
        intro={
          <>
            Changes to the Retail Operations Platform that may affect compliance. Each one is flagged when it is reported or
            built; a compliance reviewer then confirms the impact or clears it, with a note. Open an item to see what it is,
            why it was flagged, what has been done and what has not.
          </>
        }
      />
      <ReviewerNotice />
      <FilterChips
        label="Show"
        value={view}
        onChange={(v) => setParams(v === 'needs-review' ? {} : { view: v }, { replace: true })}
        options={VIEWS.map((v) => ({ value: v.value, label: v.label, count: countFor(v.value) }))}
      />
      {error && (
        <div className="banner banner--err" role="alert">
          {error}
        </div>
      )}
      {items === null && <p className="empty">Loading…</p>}
      {items !== null && items.length === 0 && !error && (
        <EmptyState title={viewDef.empty} icon="check">
          {view === 'needs-review' && 'New items appear here when a change is flagged. Reviewers also get one Teams message a day while something is waiting.'}
        </EmptyState>
      )}
      {items && items.length > 0 && (
        <ul className="cr-list" aria-label={viewDef.label}>
          {items.map((r) => (
            <Row key={r.key} r={r} view={view} />
          ))}
        </ul>
      )}
    </div>
  );
}
